// The wire of the identity verbs a placed browser speaks and the sign-in
// profiles it reads first (02-DESIGN/01-identity/02-the-auth-service.md § the
// identity carrier, § the HTTPS surfaces; 03-signing-in.md § the profile
// comes first). A browser bears a carrier and never verifies one.
import { headers } from "@nats-io/nats-core";
import { base32Encode, base64UrlDecode, utf8Decode, utf8Encode } from "./encoding.js";
import { request } from "./request.js";
import { address, client, Family, join, validSlug, validToken } from "./subject.js";
/** SERVICE is the auth service's micro service name, under which callers discover its sealing key. */
export const SERVICE = "auth";
/** Role is a scoped signing key of a tenant account. */
export const Role = {
    admin: "admin",
    member: "member",
    app: "app",
    workload: "workload",
    /**
     * The further scoped keys a platform-provided workload is placed under
     * (02-DESIGN/04-workloads/02-the-fleet.md § the platform's own workloads;
     * 02-DESIGN/10-state/02-the-state-service.md § a platform workload under its own key).
     */
    vault: "vault",
    state: "state",
    observer: "observer",
};
/** isRole reports whether s names a scoped key. */
export function isRole(s) {
    return Object.values(Role).includes(s);
}
/** The attestation keys, in the order the grammar fixes. */
const ATTESTATION_KEYS = ["role", "principal", "app", "workload", "placement"];
const MAX_ATTESTATIONS = 256;
const MAX_ATTESTATION_VALUE = 64;
/** AttestationsError is a user that does not follow the attestations' grammar. */
export class AttestationsError extends Error {
    constructor(message = "identity: attestations malformed") {
        super(message);
        this.name = "AttestationsError";
    }
}
/**
 * parseAttestations reads a connection's user — `whoAmI`'s `user`, the
 * request info's — under decision 0029's grammar: space-separated `key:value`
 * pairs of platform-minted tokens, the role first and the rest in order, each
 * at most once. A user with no colon — a directly signed credential's public
 * key — attests nothing, and answers undefined. The counterpart of
 * impire-sdk-go's identity.ParseAttestations.
 */
export function parseAttestations(user) {
    if (!user.includes(":")) {
        return undefined;
    }
    if (user.length > MAX_ATTESTATIONS) {
        throw new AttestationsError();
    }
    const out = {};
    let next = 0;
    const pairs = user.split(" ");
    for (let i = 0; i < pairs.length; i++) {
        const pair = pairs[i] ?? "";
        const at = pair.indexOf(":");
        if (at <= 0) {
            throw new AttestationsError();
        }
        const k = pair.slice(0, at);
        const v = pair.slice(at + 1);
        if (v === "" || v.length > MAX_ATTESTATION_VALUE || v.includes(":") || !validToken(v)) {
            throw new AttestationsError();
        }
        const j = ATTESTATION_KEYS.indexOf(k, next);
        if (j < 0 || (i === 0) !== (k === "role")) {
            throw new AttestationsError();
        }
        next = j + 1;
        out[k] = v;
    }
    const role = out["role"] ?? "";
    if (!isRole(role)) {
        throw new AttestationsError();
    }
    return { ...out, role };
}
/** CARRIER_HEADER carries the identity carrier on every request a placed connection sends. */
export const CARRIER_HEADER = "Impire-Carrier";
/**
 * ASSERTED_USER_HEADER carries what an app asserted about its end user, beside
 * the carrier and outside its signature: a claim, recorded as such.
 */
export const ASSERTED_USER_HEADER = "Impire-Asserted-User";
/** The identity verbs, in their tokenless form. */
export const Verb = {
    /** Mints a carrier addressed to one audience for the placed connection, read from the request info. */
    carrier: "carrier",
    /** Publishes the current and previous carrier keys for offline verification. */
    keys: "keys",
    /** Lists, on the identity plane, the tenants a person has a row in. */
    memberships: "memberships",
    /** Creates a tenant on the identity plane. */
    createTenant: "create-tenant",
    /** Presents a root's entitlement, `identity.entitlement.put` (decision 0070). */
    entitlement: "entitlement",
};
/** carrierSubject is `identity.carrier`. */
export function carrierSubject() {
    return client(Family.identity, Verb.carrier);
}
/** keysSubject is `identity.keys`. */
export function keysSubject() {
    return client(Family.identity, Verb.keys);
}
/**
 * audienceOf is the audience of a request on a subject: the subject's family,
 * or for a workload's family `workload.<id>`. A client derives it from the
 * subject it publishes on, never from anything a server advertises.
 */
export function audienceOf(subject) {
    let toks;
    try {
        toks = address(subject);
    }
    catch {
        return "";
    }
    const [family = "", id] = toks;
    if (family === Family.workload) {
        return id === undefined ? "" : workloadAudience(id);
    }
    return family;
}
/** workloadAudience is the audience of one workload's family, `impire.workload.<id>`. */
export function workloadAudience(id) {
    // The audience is the address, not the wire subject: it names the family
    // and never the root (decision 0030).
    return join(Family.workload, id);
}
/** requestCarrier asks `identity.carrier`, sealed, and returns the carrier. */
export function requestCarrier(nc, caller, req, opts) {
    return request(nc, caller, SERVICE, carrierSubject(), req, opts);
}
/** withCarrier stamps the carrier on a request's headers, making them when absent. */
export function withCarrier(carrier, hdrs) {
    const h = hdrs ?? headers();
    h.set(CARRIER_HEADER, carrier);
    return h;
}
/** fetchKeys reads the current and previous carrier keys on `identity.keys`, sealed. */
export function fetchKeys(nc, caller, opts) {
    return request(nc, caller, SERVICE, keysSubject(), undefined, opts);
}
/**
 * Carriers keeps the carriers a placed connection stamps, one per audience
 * (hq decision 0024): each asked of the auth service from the placed
 * connection — the request info is the proof, no credential is presented —
 * kept until it nears expiry and asked for again after. Concurrent asks for
 * one audience share one request.
 */
export class Carriers {
    /** Milliseconds before its expiry a carrier is asked for again; a minute by default. */
    margin = 60_000;
    /** Milliseconds the request to the auth service is bounded by; five seconds by default. */
    timeout = 5000;
    nc;
    caller;
    held = new Map();
    inflight = new Map();
    constructor(nc, caller) {
        this.nc = nc;
        this.caller = caller;
    }
    /** forAudience is the connection's carrier addressed to audience. */
    forAudience(audience) {
        const h = this.held.get(audience);
        if (h !== undefined && h.expires - Date.now() > this.margin) {
            return Promise.resolve(h.token);
        }
        let p = this.inflight.get(audience);
        if (p === undefined) {
            p = this.ask(audience).finally(() => {
                this.inflight.delete(audience);
            });
            this.inflight.set(audience, p);
        }
        return p;
    }
    /** forSubject is the carrier for a request on subject. */
    forSubject(subject) {
        return this.forAudience(audienceOf(subject));
    }
    /** carrier is a CarrierSource: the carrier for the audience it is asked with. */
    carrier = (audience) => {
        if (audience === undefined || audience === "") {
            return Promise.reject(new CarrierError("identity: carrier: no audience"));
        }
        return this.forAudience(audience);
    };
    async ask(audience) {
        const reply = await requestCarrier(this.nc, this.caller, { audience }, { timeout: this.timeout });
        if (reply.carrier === "") {
            throw new CarrierError("identity: carrier: the auth service answered no carrier");
        }
        const now = Date.now();
        for (const [k, v] of this.held) {
            if (v.expires <= now) {
                this.held.delete(k);
            }
        }
        this.held.set(audience, { token: reply.carrier, expires: reply.exp * 1000 });
        return reply.carrier;
    }
}
/** CarrierError is thrown for a carrier that cannot be read. */
export class CarrierError extends Error {
    constructor(message = "identity: carrier malformed", options) {
        super(message, options);
        this.name = "CarrierError";
    }
}
/**
 * decodeCarrier reads a carrier without verifying it —
 * `base64url(claims).base64url(signature)` — for the display a page shows and
 * never for authorization.
 */
export function decodeCarrier(token) {
    const dot = token.indexOf(".");
    if (dot <= 0) {
        throw new CarrierError();
    }
    try {
        return JSON.parse(utf8Decode(base64UrlDecode(token.slice(0, dot))));
    }
    catch (err) {
        throw new CarrierError("identity: carrier malformed", { cause: err });
    }
}
// The principal token: the one subject token a person is where one is needed
// (02-DESIGN/01-identity/01-tenants-and-roles.md § who a principal is) — their
// own vault namespace, `person.<principal>.>`, where the console deposits a
// linked toolkit's credential (02-DESIGN/08-toolkits/03-linking.md § the
// namespace).
/**
 * PRINCIPAL_TAG is the tag the auth service stamps on every person's
 * placement, carrying their principal token; the member and admin templates
 * expand it into the person's own vault namespace, `person.{{tag(principal)}}.>`.
 */
export const PRINCIPAL_TAG = "principal";
/**
 * principalToken is the lowercase base32 encoding, without padding, of the
 * first 128 bits of a SHA-256 digest over the issuer or lane, a newline, and
 * the stable subject — 26 characters, every one a subject token character. On
 * the oidc lane issuerOrLane is the provider's issuer URL exactly as its
 * discovery document states it; on the github lane it is the lane, `github`.
 * The token is derived, never stored, the same for the same person at every
 * sign-in, and reveals nothing; the Go library computes it the same way. An
 * app's and a workload's tokens are their ids and never pass through here.
 */
export async function principalToken(issuerOrLane, stableSubject) {
    const digest = await crypto.subtle.digest("SHA-256", utf8Encode(`${issuerOrLane}\n${stableSubject}`).slice());
    return base32Encode(new Uint8Array(digest, 0, 16));
}
// Profiles are what a client reads before it signs in
// (02-DESIGN/01-identity/02-the-auth-service.md § the HTTPS surfaces).
/** PLATFORM_PROFILE_PATH is the platform profile's path at the install's name. */
export const PLATFORM_PROFILE_PATH = "/.well-known/impire/platform.json";
/** MAX_PROFILE_SIZE bounds a profile. */
export const MAX_PROFILE_SIZE = 64 * 1024;
/** tenantProfilePath is a tenant's profile path at the install's name. */
export function tenantProfilePath(slug) {
    return `/.well-known/impire/${slug}/sign-in.json`;
}
/** Surfaces a provider configuration allows to sign in. */
export const Surface = {
    browser: "browser",
    terminal: "terminal",
};
/** Exchange says whether the browser exchanges its code itself or through the token-exchange endpoint. */
export const Exchange = {
    public: "public",
    serverSide: "server-side",
};
/** offersReplicas reports whether the install offers a store n copies: one always, any other count only where store_replicas lists it. */
export function offersReplicas(p, n) {
    return n === 1 || (p.store_replicas ?? []).includes(n);
}
/**
 * Image names the platform's images, the keys of PlatformProfile.images: the
 * console registers a platform workload with the reference it reads under the
 * name (decision 0017).
 */
export const Image = {
    vault: "vault",
    inference: "inference",
    toolkitMCP: "toolkit-mcp",
    state: "state",
};
/** IMAGE_NAMES lists the platform's image names. */
export const IMAGE_NAMES = Object.values(Image);
/** validImageName reports whether name is one of the platform's image names. */
export function validImageName(name) {
    return IMAGE_NAMES.includes(name);
}
/** ProfileError is thrown for a profile that could not be read under the rules. */
export class ProfileError extends Error {
    constructor(message, options) {
        super(`identity: profile: ${message}`, options);
        this.name = "ProfileError";
    }
}
/** MAX_REDIRECTS bounds the same-host redirects a read follows. */
export const MAX_REDIRECTS = 3;
function isLoopback(host) {
    return (host === "localhost" ||
        host === "127.0.0.1" ||
        host === "::1" ||
        host === "[::1]" ||
        host.startsWith("127."));
}
function allowed(u) {
    return u.protocol === "https:" || (u.protocol === "http:" && isLoopback(u.hostname));
}
async function readBounded(resp, max) {
    if (resp.body === null) {
        const whole = new Uint8Array(await resp.arrayBuffer());
        if (whole.length > max) {
            throw new ProfileError(`over ${max} bytes`);
        }
        return whole;
    }
    const reader = resp.body.getReader();
    const chunks = [];
    let total = 0;
    for (;;) {
        const { done, value } = await reader.read();
        if (done) {
            break;
        }
        total += value.length;
        if (total > max) {
            await reader.cancel();
            throw new ProfileError(`over ${max} bytes`);
        }
        chunks.push(value);
    }
    const out = new Uint8Array(total);
    let at = 0;
    for (const c of chunks) {
        out.set(c, at);
        at += c.length;
    }
    return out;
}
/**
 * readProfile fetches a profile at url: https only (http is allowed on
 * loopback), a redirect to another host refused — and in a browser, whose
 * fetch hides a redirect's target, any redirect — the size bounded.
 */
export async function readProfile(url, opts = {}) {
    let parsed;
    try {
        parsed = new URL(url);
    }
    catch (err) {
        throw new ProfileError(`${url} is not a URL`, { cause: err });
    }
    if (!allowed(parsed)) {
        throw new ProfileError(`${url} is not https`);
    }
    const f = opts.fetch ?? fetch;
    let current = parsed;
    for (let redirects = 0;; redirects++) {
        let resp;
        try {
            resp = await f(current.toString(), {
                redirect: "manual",
                signal: opts.signal ?? null,
                headers: { accept: "application/json" },
            });
        }
        catch (err) {
            throw new ProfileError(`${url}: ${err instanceof Error ? err.message : String(err)}`, { cause: err });
        }
        if (resp.type === "opaqueredirect" || (resp.status >= 300 && resp.status < 400)) {
            const location = resp.headers.get("location");
            if (location === null) {
                throw new ProfileError("redirect refused");
            }
            let next;
            try {
                next = new URL(location, current);
            }
            catch (err) {
                throw new ProfileError("redirect refused", { cause: err });
            }
            if (next.hostname !== parsed.hostname) {
                throw new ProfileError("redirect to another host refused");
            }
            if (redirects >= MAX_REDIRECTS) {
                throw new ProfileError("too many redirects");
            }
            if (!allowed(next)) {
                throw new ProfileError(`${next.toString()} is not https`);
            }
            current = next;
            continue;
        }
        if (resp.status !== 200) {
            throw new ProfileError(`${url} answered ${resp.status}`);
        }
        const body = await readBounded(resp, MAX_PROFILE_SIZE);
        try {
            return JSON.parse(utf8Decode(body));
        }
        catch (err) {
            throw new ProfileError(`${url} is not JSON`, { cause: err });
        }
    }
}
/** readPlatformProfile reads the install's profile at the origin. */
export function readPlatformProfile(origin, opts) {
    return readProfile(new URL(PLATFORM_PROFILE_PATH, origin).toString(), opts);
}
/** readTenantProfile reads one tenant's profile at the origin; the slug is validated before it enters a path. */
export function readTenantProfile(origin, slug, opts) {
    if (!validSlug(slug)) {
        return Promise.reject(new ProfileError(`${slug} is not a slug`));
    }
    return readProfile(new URL(tenantProfilePath(slug), origin).toString(), opts);
}
// The identity plane: a placement in the platform account whose only subjects
// carry the lane and the person's stable subject as tokens, so that the two
// verbs behind them read who is asking from a permission the broker enforced
// (02-DESIGN/01-identity/03-signing-in.md § sign-up). The stable subject is
// carried as the lowercase hex SHA-256 of its bytes, so the subject on the
// wire is `identity.plane.<lane>.<hash>.<verb>`; a browser learns its lane and
// hash from the one publish entry its placement's permissions carry.
/** VERB_PLANE is the token under which the identity plane's verbs live. */
export const VERB_PLANE = "plane";
/** planeSubject builds `identity.plane.<lane>.<hash>.<verb>` for a verb of the identity plane. */
export function planeSubject(lane, hash, verb) {
    return client(Family.identity, VERB_PLANE, lane, hash, verb);
}
/**
 * planeOf reads the lane and the hash back from the one publish entry the
 * plane template expands, `identity.plane.<lane>.<hash>.>`, among a
 * placement's publish permissions as `whoAmI` reports them; undefined when
 * the placement is not on the plane.
 */
export function planeOf(allow) {
    for (const entry of allow) {
        let toks;
        try {
            toks = address(entry);
        }
        catch {
            continue;
        }
        const [family, plane, lane, hash, rest] = toks;
        if (toks.length === 5 &&
            family === Family.identity &&
            plane === VERB_PLANE &&
            rest === ">" &&
            lane !== undefined &&
            hash !== undefined &&
            validToken(lane) &&
            validToken(hash)) {
            return { lane, hash };
        }
    }
    return undefined;
}
// The audit records the auth service writes, on `audit.<tenant>.signin.<outcome>`
// (02-DESIGN/01-identity/02-the-auth-service.md § the audit records): the admin
// template admits a subscription to the tenant's own family, the member
// template a member's own records. The tenant is the family's own second
// token, admitted on the tenant's own slug — not an account slot.
/** The audit outcomes of a sign-in record. */
export const Outcome = {
    placed: "placed",
    refused: "refused",
    ended: "ended",
};
/** auditSubject is the subject a sign-in record is written on: `audit.<tenant>.signin.<outcome>`. */
export function auditSubject(tenant, outcome) {
    return client(Family.audit, tenant, "signin", outcome);
}
/** auditFilter is the subscription a console pane makes for a tenant's sign-in records: `audit.<tenant>.signin.>`. */
export function auditFilter(tenant) {
    return client(Family.audit, tenant, "signin", ">");
}
/** PLATFORM_TENANT is the token the platform's own records are written under. */
export const PLATFORM_TENANT = "platform";
// The service attestation (decision 0026): the auth service's voucher for
// what one placement advertises on discovery — its tenant, its workload, its
// placement, its sealing key and the names its registration declares —
// signed under the carrier key, behind a domain of its own so that it is never
// accepted as a carrier, nor a carrier as one
// (02-DESIGN/04-workloads/01-the-workload-model.md § what a workload serves).
// The browser verifies it before it keeps a discovered model or toolkit.
/** ATTESTATION_TYPE is the type an attestation names, which no carrier does. */
export const ATTESTATION_TYPE = "service-attestation";
/** METADATA_ATTESTATION is the service-info metadata entry that advertises a placement's attestation, beside its sealing key. */
export const METADATA_ATTESTATION = "attestation";
/** PLATFORM_NAMES are the names only the platform answers discovery under; the broker fences them. */
export const PLATFORM_NAMES = [
    "identity",
    SERVICE,
    "workloads",
    "config",
    "guard",
    "vault",
    "state",
    "observer",
    "blueprint",
    "steward",
    "solutions",
    "mail",
    "billing",
];
/** AttestationError is an attestation that does not verify. */
export class AttestationError extends Error {
    constructor(message = "identity: attestation invalid", options) {
        super(message, options);
        this.name = "AttestationError";
    }
}
/** keySet is the key set of one `identity.keys` reply: the current key and, during a rotation, the previous one. */
export function keySet(reply) {
    const keys = new Map();
    for (const k of [reply.current, reply.previous]) {
        if (k !== undefined) {
            keys.set(k.kid, base64UrlDecode(k.key));
        }
    }
    return { key: (kid) => keys.get(kid) };
}
/** The domain prefixed to the claims an attestation's signature covers, and to nothing a carrier's does. */
const ATTESTATION_DOMAIN = utf8Encode("impire-service-attestation\x00");
/**
 * verifyAttestation checks an attestation offline against the carrier keys:
 * its signature under its own domain, its type, and its expiry, which every
 * attestation carries. now is in milliseconds.
 */
export async function verifyAttestation(token, keys, now = Date.now()) {
    const dot = token.indexOf(".");
    if (dot <= 0) {
        throw new AttestationError("identity: attestation malformed");
    }
    let claims;
    let sig;
    let a;
    try {
        claims = base64UrlDecode(token.slice(0, dot));
        sig = base64UrlDecode(token.slice(dot + 1));
        a = JSON.parse(utf8Decode(claims));
    }
    catch (err) {
        throw new AttestationError("identity: attestation malformed", { cause: err });
    }
    const raw = keys.key(a.kid);
    if (raw === undefined) {
        throw new AttestationError("identity: carrier key unknown");
    }
    const signed = new Uint8Array(ATTESTATION_DOMAIN.length + claims.length);
    signed.set(ATTESTATION_DOMAIN, 0);
    signed.set(claims, ATTESTATION_DOMAIN.length);
    let ok;
    try {
        const key = await crypto.subtle.importKey("raw", raw, { name: "Ed25519" }, false, [
            "verify",
        ]);
        ok = await crypto.subtle.verify({ name: "Ed25519" }, key, sig, signed);
    }
    catch (err) {
        throw new AttestationError("identity: attestation signature invalid", { cause: err });
    }
    if (!ok) {
        throw new AttestationError("identity: attestation signature invalid");
    }
    if (a.typ !== ATTESTATION_TYPE) {
        throw new AttestationError("identity: attestation malformed");
    }
    if (typeof a.exp !== "number" || a.exp === 0 || Math.floor(now / 1000) >= a.exp) {
        throw new AttestationError("identity: attestation expired");
    }
    return a;
}
/** vouches reports whether the attestation vouches an answer: the caller's own tenant, the name the caller resolved, and the workload and the key the answer advertises. */
export function vouches(a, tenant, name, workload, key) {
    return (a.tenant !== "" &&
        a.tenant === tenant &&
        a.workload !== "" &&
        a.workload === workload &&
        a.sealing_key !== "" &&
        a.sealing_key === key &&
        (a.names ?? []).includes(name));
}
/**
 * judge is vouched with the cutover's middle verdict: an answer with no
 * attestation is `unattested` rather than `refused`, which a discovery that
 * is lenient keeps for a name no attested answer carries (decision 0026,
 * the two-step cutover).
 */
export async function judge(md, keys, tenant, name, workload, now = Date.now()) {
    const token = md[METADATA_ATTESTATION];
    if (token === undefined || token === "") {
        return "unattested";
    }
    return (await vouched(md, keys, tenant, name, workload, now)) ? "attested" : "refused";
}
/**
 * vouched verifies the attestation a discovery answer advertises and reports
 * whether it vouches the answer for name in tenant: what every discovery
 * keeps an answer for a workload's name by, dropping any other uncounted.
 */
export async function vouched(md, keys, tenant, name, workload, now = Date.now()) {
    const token = md[METADATA_ATTESTATION];
    const key = md["sealing_key"];
    if (keys === undefined || typeof token !== "string" || typeof key !== "string") {
        return false;
    }
    try {
        return vouches(await verifyAttestation(token, keys, now), tenant, name, workload, key);
    }
    catch {
        return false;
    }
}
// --- the plan (02-DESIGN/17-plans/01-the-entitlement.md § what
// identity.tenant.get answers, § presenting it; 02-features-and-limits.md
// § the registry, § the console; decision 0070). Mirrors impire-sdk-go
// identity/plan.go, entitlement.go and the Tenant of tenant.go. The browser
// presents a plan key and reads the plan back; it never verifies one.
/** The features of the registry. */
export const Feature = {
    ownProvider: "own-provider",
    manageWorkspaces: "manage-workspaces",
    publishInstall: "publish-install",
    hostCapabilities: "host-capabilities",
};
/** The limits of the registry. */
export const Limit = {
    storage: "storage",
    workspaces: "workspaces",
    invites: "invites",
    workloads: "workloads",
    vcpus: "vcpus",
    memory: "memory",
};
/** The two kinds of a registry entry. */
export const RegistryKind = { feature: "feature", limit: "limit" };
/** The units a limit is stated in; a feature has none. */
export const Unit = { bytes: "bytes", count: "count", countPerDay: "count/day" };
/** REGISTRY is the release's registry, in the design's order. */
export const REGISTRY = [
    { name: Feature.ownProvider, kind: RegistryKind.feature },
    { name: Feature.manageWorkspaces, kind: RegistryKind.feature },
    { name: Feature.publishInstall, kind: RegistryKind.feature },
    { name: Feature.hostCapabilities, kind: RegistryKind.feature },
    { name: Limit.storage, kind: RegistryKind.limit, unit: Unit.bytes },
    { name: Limit.workspaces, kind: RegistryKind.limit, unit: Unit.count },
    { name: Limit.invites, kind: RegistryKind.limit, unit: Unit.countPerDay },
    { name: Limit.workloads, kind: RegistryKind.limit, unit: Unit.count },
    { name: Limit.vcpus, kind: RegistryKind.limit, unit: Unit.count },
    { name: Limit.memory, kind: RegistryKind.limit, unit: Unit.bytes },
];
/** lookup answers a registry entry by name, undefined for a name the registry does not know. */
export function lookup(name) {
    return REGISTRY.find((e) => e.name === name);
}
/** NO_BOUND is a limit's value where nothing bounds it, as the relation's limits write it. */
export const NO_BOUND = -1;
/**
 * The refusals a plan causes, as the error code a verb answers with.
 * `licenseExpired` is the install license's: every creation of a workspace
 * past its grace. A solution's license has its own codes, in `solution`.
 */
export const PlanCode = {
    notInPlan: "not-in-plan",
    limit: "limit",
    notARoot: "not-a-root",
    licenseExpired: "license-expired",
};
/** The refusals of `identity.entitlement.put`, in the order verification checks them. */
export const EntitlementCode = {
    /** The shape, the type, the key, the signature or the issuer: not signed by Impire. */
    invalid: "invalid",
    /** Issued for another install. */
    wrongInstall: "wrong-install",
    /** Issued for another workspace, or the install's license presented as a workspace's. */
    wrongWorkspace: "wrong-workspace",
    /** Past its expiry and its grace. */
    expired: "expired",
    /** Issued no later than the one applied. */
    stale: "stale",
};
/**
 * The sources of an effective value: the root's entitlement, the install's
 * table for the plan's name, the install's default, or the blueprint where
 * the parent narrowed it.
 */
export const Source = {
    entitlement: "entitlement",
    plan: "plan",
    default: "default",
    blueprint: "blueprint",
};
/** The states of a plan. A root on a default plan with no trial duration and no document is active with no end. */
export const PlanState = {
    active: "active",
    trial: "trial",
    grace: "grace",
    lapsed: "lapsed",
};
/** granted reports whether the feature is granted; an unknown name, or no plan answered, is not. */
export function granted(e, feature) {
    return e?.features[feature]?.granted === true;
}
/**
 * limit answers a limit's effective value, undefined where nothing bounds
 * it (NO_BOUND) or the limit is not answered.
 */
export function limit(e, name) {
    const v = e?.limits[name];
    if (v === undefined || v.value < 0) {
        return undefined;
    }
    return v.value;
}
/** entitlementPutSubject is `identity.entitlement.put`. */
export function entitlementPutSubject() {
    return client(Family.identity, Verb.entitlement, "put");
}
/**
 * putEntitlement presents a root's plan key on `identity.entitlement.put`,
 * sealed; the reply is the workspace as `identity.tenant.get` now answers it,
 * so the page re-renders from it. A refusal is a ServiceError whose code is
 * one of EntitlementCode, or PlanCode.notARoot on a child.
 */
export async function putEntitlement(nc, caller, token, opts) {
    const body = { token };
    const r = await request(nc, caller, SERVICE, entitlementPutSubject(), body, opts);
    return r.tenant;
}
//# sourceMappingURL=identity.js.map